import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { withStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { MdError, MdRefresh } from 'react-icons/md';

import actions from '../actions';
import * as selectors from '../selectors';

const styles = (theme) => ({
  container: {
    display: 'flex',
    height: '70vh',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '500px',
    width: '100%',
  },
  errorPaper: {
    padding: theme.spacing(4),
    textAlign: 'center',
    width: '100%',
  },
  errorIcon: {
    fontSize: '64px',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  errorTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main,
  },
  errorDescription: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  alert: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
});

function CallBack({ classes, logIntoClient, country, accountId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('location.hash', location.hash);
    
    // Parse the hash for error parameters
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const errorParam = hashParams.get('error');
    const errorDescription = hashParams.get('error_description');
    
    if (errorParam) {
      setError({
        type: errorParam,
        description: errorDescription || 'An error occurred during authentication.',
      });
      setIsLoading(false);
    } else {
      // No error, proceed with normal login flow
      logIntoClient(location.hash);
    }
  }, [location.hash, logIntoClient]);

  const handleRetry = () => {
    setIsLoading(true);
    setError(null);
    // Navigate back to the main page to retry
    navigate('/');
  };

  const handleGoHome = () => {
    // Navigate to home with country code and accountID
    if (country && accountId) {
      navigate(`/${country}/${accountId}`);
    } else {
      // Fallback to root if country or accountId is not available
      navigate('/');
    }
  };

  const getErrorContent = () => {
    if (!error) return null;

    let title = 'Authentication Error';
    let severity = 'error';
    let icon = <MdError className={classes.errorIcon} />;
    let description = error.description;

    switch (error.type) {
      case 'unauthorized':
        title = 'Unauthorized Access';
        description = 'Your email or verification code is incorrect. Please check your <b>inbox</b> and use a <b>latest passwordless link</b> to log in.';
        break;
      case 'session_expired':
        title = 'Session Expired';
        description = 'Your session has expired. Please log in again to continue.';
        break;
      case 'access_denied':
        title = 'Access Denied';
        description = 'You do not have permission to access this resource.';
        break;
      default:
        title = 'Authentication Error';
        description = error.description || 'An unexpected error occurred during authentication.';
    }

    return (
      <div className={classes.errorContainer}>
        <Paper elevation={3} className={classes.errorPaper}>
          {icon}
          <Typography variant="h4" className={classes.errorTitle}>
            {title}
          </Typography>
          <Typography variant="body1" className={classes.errorDescription}>
            {!!description && <div dangerouslySetInnerHTML={{ __html: description }} />}
          </Typography>
          
          <Alert severity={severity} className={classes.alert}>
            <Typography variant="body2">
              If you continue to experience issues, please contact support or try again later.
            </Typography>
          </Alert>

          <Box className={classes.actionButtons}>
            {/* <Button
              variant="contained"
              color="primary"
              onClick={handleRetry}
              startIcon={<MdRefresh />}
            >
              Try Again
            </Button> */}
            <Button
              variant="outlined"
              color="primary"
              onClick={handleGoHome}
            >
              Go to Home
            </Button>
          </Box>
        </Paper>
      </div>
    );
  };

  if (error) {
    return (
      <div className={classes.container}>
        {getErrorContent()}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <CircularProgress />
    </div>
  );
}

export default connect(
  (state, _) => ({
    country: selectors.getCountry(state),
    accountId: selectors.getAccountId(state),
  }),
  (dispatch, _) => ({
    logIntoClient(hash) {
      dispatch(actions.app.auth.logIntoClient(hash));
    },
  }),
)(withStyles(styles)(CallBack));
